/** @jsx jsx */
import { css, jsx } from "@emotion/react"
// React must stay imported: with the emotion `@jsx jsx` pragma and no
// `@jsxFrag`, the `<>` fragment shorthand below compiles to `React.Fragment`,
// so the default React import has to be in scope or the component throws
// "React is not defined" at render. (#230 removed it as "unused" by mistake.)
import React, { Fragment, useState, useRef, useContext, useEffect } from "react"
import { connectSearchBox } from "react-instantsearch-dom"
import SearchIcon from "../../assets/icons/search.svg"
import Icon from "../../assets/icons/close-alt.svg"
import SearchContext from "../../context/Search/searchContext"
import { useLocation } from "@reach/router"
import { Dialog, Transition } from "@headlessui/react"
import { navigate } from "gatsby"
import { filterBodyToSearch } from "../../helpers/assetSearchUrl"
import { useMediaQuery } from "react-responsive"
import "../../styles/search.css"

const useFocus = () => {
  const htmlElRef = useRef(null)
  const setFocus = () => {
    htmlElRef.current && htmlElRef.current.focus()
  }

  return [htmlElRef, setFocus]
}
const SearchBoxC = ({ refVal, setFocus }) => {
  const [inputRef, setInputFocus] = useFocus()
  // Drive the input from local state so typing does NOT fire Algolia queries.
  // Navigation on Enter uses this local value directly.
  const [inputValue, setInputValue] = useState("")
  useEffect(() => {
    setInputFocus(true)
  }, [])
  const { setSearchModal } = useContext(SearchContext)

  return (
    <div className="relative">
      <input
        ref={inputRef}
        // type="search"
        value={inputValue}
        onChange={event => setInputValue(event.currentTarget.value)}
        onKeyPress={event => {
          // Enter lands on /assets so results persist and browser Back works,
          // reaching parity with the on-page search. The shared serializer keeps
          // the search param identical to what the /assets page reads.
          if (event.key === "Enter" && inputValue && inputValue.trim()) {
            navigate("/assets?" + filterBodyToSearch({ search: inputValue.trim() }))
            setSearchModal(false)
          }
        }}
        className={`bg-white ring-1 ring-[#EBEBEB] focus:ring-[#EBEBEB] shadow-[0px_6px_20px_rgba(34,34,34,0.05)] block w-full h-[70px] pl-8 pr-20 border-0 rounded-full focus:outline-none text-reg-16`}
        css={css`
          &&& {
            min-width: 230px;
            /* font-size: 17px; */
          }
        `}
        placeholder="Start typing to search..."
        autoFocus={true}
        onFocus={e => {
          setFocus(true)
          e.currentTarget.select()
        }}
        onBlur={e => {
          if (!document.activeElement === refVal.current) setFocus(false)
        }}
        reset={null}
      />
      <button
        className="absolute inset-y-0 right-0 pr-7 flex items-center"
        onClick={() => {
          if (inputValue && inputValue.trim()) {
            navigate("/assets?" + filterBodyToSearch({ search: inputValue.trim() }))
            setSearchModal(false)
          }
        }}
      >
        <SearchIcon
          className="h-5 w-5"
          aria-hidden="true"
          css={css`
            path {
            }
          `}
        />
      </button>
    </div>
  )
}

const CustomSearchBox = connectSearchBox(props => (
  <SearchBoxC {...props} reset={null} />
))
function SearchModal({ isOpen, setIsOpen }) {
  const location = useLocation()
  const ref = useRef(null)
  const [, setFocus] = useState(false)
  const { setSearchModal } = useContext(SearchContext)
  useEffect(() => {
    setSearchModal(false)
  }, [location])

  const isMobile = useMediaQuery({
    query: "(max-width: 768px)",
  })

  return (
    <>
      <Transition
        appear
        show={isOpen}
        as={Fragment}
        enter="ease-out duration-200"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="ease-out duration-500"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <Dialog
          as="div"
          className="fixed inset-0 z-[1000] overflow-auto"
          onClose={() => setIsOpen(false)}
        >
          <div className="min-h-screen text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-out duration-500"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay
                className="fixed inset-0 bg-[rgba(0,0,0,.7)] md:bg-white"
                // css={css`
                //   &&& {
                //     background: #fff;
                //     /* background: rgba(0, 0, 0, 0.7); */
                //   }
                // `}
              />
            </Transition.Child>

            {/* This element is to trick the browser into centering the modal contents. */}
            {/* <span
              className="inline-block h-screen align-middle"
              aria-hidden="true"
            >
              &#8203;
            </span> */}
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom={
                isMobile ? `opacity-0 translate-y-full` : `opacity-0 scale-95`
              }
              enterTo={
                isMobile ? `opacity-100 translate-y-0` : `opacity-100 scale-100`
              }
              leave="ease-out duration-200"
              leaveFrom={
                isMobile ? `opacity-100 translate-y-0` : `opacity-100 scale-100`
              }
              leaveTo={
                isMobile ? `opacity-0 translate-y-full` : `opacity-0 scale-95`
              }
            >
              <div
                className={`
                  bg-white absolute bottom-0 -left-[1px] -right-[1px] border border-[#EBEBEB] rounded-[15px_15px_0_0]
                  md:relative md:rounded-none md:border-none md:left-auto md:right-auto md:bottom-auto md:bg-transparent w-full mx-auto z-[9999999] text-left transition-all transform
                  flex md:inline-block flex-col h-full overflow-hidden md:overflow-auto
                `}
              >
                <div className="border-b border-[#EBEBEB] py-[10px] sticky top-0 z-[999] bg-white">
                  <div className="w-[90%] mx-auto  flex items-center">
                    {isMobile ? (
                      <h1 className="text-strong-20">Search</h1>
                    ) : (
                      <h1 className="text-strong-25">
                        What are you looking for?
                      </h1>
                    )}
                    <div className="ml-auto">
                      <button
                        type="button"
                        className="flex justify-center items-center bg-[#F7F7F7] rounded-[8px] h-[40px] w-[40px] ring-1 ring-offset-2 ring-transparent hover:ring-[#222] transition-all"
                        onClick={() => setIsOpen(false)}
                      >
                        <Icon className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="w-[90%] mx-auto">
                  <div className="pt-[35px] pb-[35px]">
                    <CustomSearchBox
                      setFocus={setFocus}
                      refVal={ref}
                      reset={null}
                    />
                    <p className="mt-[20px] px-2 text-reg-15 text-[#717171]">
                      Press Enter to see all matching assets on the search
                      results page.
                    </p>
                  </div>
                </div>
                {/*</div>*/}
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </>
  )
}
export default SearchModal
